import type { Page as TilelistPage } from '@shared/types/Tilelist';
import {
    calculateLastPage,
    calculateLastPageOffset,
    calculateNextOffset,
    calculatePage,
    calculatePreviousOffset,
    getPagination,
    isOffsetInLastPage,
} from '@tilelist/domain/composition/Pagination';
import type { Interval, NavButton, PageSegment, PaginationWithSegments, Segments } from './Segments.types';

type SegmentParams = {
    offset: number;
    maxItemsPerPage: number;
    itemCount: number;
    threshold: number;
};

export const segmentPreviousNavButton = ({ offset, maxItemsPerPage, threshold }: SegmentParams) => {
    const previousOffset = calculatePreviousOffset(offset, maxItemsPerPage);
    const currentPage = calculatePage(offset, maxItemsPerPage, threshold);
    if (currentPage > 1) {
        return {
            icon: 'left-arrow',
            type: 'previous',
            offset: previousOffset,
            pageNumber: calculatePage(previousOffset, maxItemsPerPage, threshold),
        } satisfies NavButton;
    }
};

export const segmentNextNavButton = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const lastPage = calculateLastPage(itemCount, maxItemsPerPage);
    const nextOffset = calculateNextOffset(offset, maxItemsPerPage, itemCount) ?? 0;
    const currentPage = calculatePage(offset, maxItemsPerPage, threshold);
    if (currentPage != lastPage || nextOffset > lastPage) {
        return {
            icon: 'right-arrow',
            type: 'next',
            offset: nextOffset,
            pageNumber: calculatePage(nextOffset, maxItemsPerPage, threshold),
        } satisfies NavButton;
    }
};

export const segmentFirstPage = ({ offset, threshold }: SegmentParams) => {
    const showFirstPage = offset >= threshold;
    if (showFirstPage) {
        return {
            pageNumber: 1,
            offset: 0,
            type: 'link',
        } satisfies PageSegment;
    }
};

export const segmentFirstInterval = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const firstOffset = 0;
    const moreThanFourPages = calculateLastPage(itemCount, maxItemsPerPage) > 4;
    const showFirstInterval = moreThanFourPages && offset > firstOffset + (maxItemsPerPage * 3 - threshold) - 1;
    if (showFirstInterval) {
        return {
            type: 'interval',
        } satisfies Interval;
    }
};

export const segmentLastInterval = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const lastPageOffset = calculateLastPageOffset(itemCount, maxItemsPerPage);
    const moreThanFourPages = calculateLastPage(itemCount, maxItemsPerPage) > 4;
    const showLastInterval = moreThanFourPages && offset <= lastPageOffset - (maxItemsPerPage * 2 + threshold);
    if (showLastInterval) {
        return {
            type: 'interval',
        } satisfies Interval;
    }
};

export const segmentPreviousPage = ({ offset, maxItemsPerPage, threshold }: SegmentParams) => {
    const previousOffset = calculatePreviousOffset(offset, maxItemsPerPage);
    const showPreviousPage = !!previousOffset;
    if (showPreviousPage) {
        return {
            pageNumber: calculatePage(previousOffset, maxItemsPerPage, threshold),
            offset: previousOffset,
            type: 'link',
        } satisfies PageSegment;
    }
};

export const segmentBeforePreviousPage = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const isLastPage = isOffsetInLastPage(offset, maxItemsPerPage, itemCount, threshold);
    const isAtLeast4Pages = offset >= maxItemsPerPage * 3;
    const previousOffset = calculatePreviousOffset(offset, maxItemsPerPage);
    const beforePreviousOffset = previousOffset ? calculatePreviousOffset(previousOffset, maxItemsPerPage) : null;
    const showBeforePreviousPage = isAtLeast4Pages && isLastPage && beforePreviousOffset !== null;
    if (showBeforePreviousPage) {
        return {
            pageNumber: calculatePage(beforePreviousOffset, maxItemsPerPage, threshold),
            offset: beforePreviousOffset,
            type: 'link',
        } satisfies PageSegment;
    }
};

export const segmentCurrentPage = ({ offset, maxItemsPerPage, threshold }: SegmentParams) => {
    return {
        pageNumber: calculatePage(offset, maxItemsPerPage, threshold),
        offset,
        type: 'link',
    } satisfies PageSegment;
};

export const segmentNextPage = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const nextOffset = calculateNextOffset(offset, maxItemsPerPage, itemCount);
    const showNextPage = !!nextOffset;
    if (showNextPage) {
        return {
            pageNumber: calculatePage(nextOffset, maxItemsPerPage, threshold),
            offset: nextOffset,
            type: 'link',
        } satisfies PageSegment;
    }
};
export const segmentAfterNextPage = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const isfirstPage = offset === 0;
    const isAtLeast4Pages = itemCount > maxItemsPerPage * 3;
    const nextOffset = calculateNextOffset(offset, maxItemsPerPage, itemCount);
    const afterNextOffset = nextOffset ? calculateNextOffset(nextOffset, maxItemsPerPage, itemCount) : null;
    const showAfterNextPage = isfirstPage && isAtLeast4Pages && afterNextOffset !== null;
    if (showAfterNextPage) {
        return {
            pageNumber: calculatePage(afterNextOffset, maxItemsPerPage, threshold),
            offset: afterNextOffset,
            type: 'link',
        } satisfies PageSegment;
    }
};

export const segmentLastPage = ({ offset, maxItemsPerPage, itemCount, threshold }: SegmentParams) => {
    const nextOffset = calculateNextOffset(offset, maxItemsPerPage, itemCount);
    const lastPageOffset = calculateLastPageOffset(itemCount, maxItemsPerPage);
    /*                                                                                  */
    const showLastPage =
        lastPageOffset !== offset && lastPageOffset !== nextOffset && nextOffset != null && lastPageOffset > nextOffset;
    if (showLastPage) {
        return {
            pageNumber: calculatePage(lastPageOffset, maxItemsPerPage, threshold),
            offset: lastPageOffset,
            type: 'link',
        } satisfies PageSegment;
    }
};

export const getSegments = (params: SegmentParams): Segments => {
    return {
        segmentPreviousNavButton: segmentPreviousNavButton(params),
        segmentFirstPage: segmentFirstPage(params),
        segmentFirstInterval: segmentFirstInterval(params),
        segmentBeforePreviousPage: segmentBeforePreviousPage(params),
        segmentPreviousPage: segmentPreviousPage(params),
        segmentCurrentPage: segmentCurrentPage(params),
        segmentNextPage: segmentNextPage(params),
        segmentAfterNextPage: segmentAfterNextPage(params),
        segmentLastInterval: segmentLastInterval(params),
        segmentLastPage: segmentLastPage(params),
        segmentNextNavButton: segmentNextNavButton(params),
    };
};

export function getPaginationWithSegments(
    counts: { rankedCount: number; sponsoredCount: number },
    page: TilelistPage,
): PaginationWithSegments {
    const PER_PAGE = 120;
    const THRESHOLD = PER_PAGE / 2;
    const basePagination = getPagination(counts, page);
    const { currentOffset } = basePagination;
    const { rankedCount, sponsoredCount } = counts;
    const perPageWithSPA = PER_PAGE - sponsoredCount;
    const itemCount = rankedCount;
    const moreThanOnePage = itemCount + sponsoredCount > perPageWithSPA;
    const segments = moreThanOnePage
        ? getSegments({
              offset: currentOffset,
              maxItemsPerPage: perPageWithSPA,
              itemCount,
              threshold: THRESHOLD,
          })
        : undefined;

    return {
        ...basePagination,
        segments,
    };
}
